import { BILLING_PERIOD_LABELS, type BillingPeriod } from '@/types/product';
import type { QuoteHoursLine } from '@/types/quoteHours';
import { formatRange } from '../../../../backend/src/utils/productInvestment';

const hoursFormat = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 2 });

/** "16 uur", of "16 – 32 uur" bij een van–tot prijs. */
export const formatHours = (hours: number, maxHours?: number) =>
  `${formatRange(hours, maxHours, (value) => hoursFormat.format(value))} uur`;

/** Kop boven een totaal: "Eenmalig" of "Per maand". */
export const periodTitle = (period: BillingPeriod) => {
  const label = BILLING_PERIOD_LABELS[period] ?? period;
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export interface QuoteHoursItemGroup {
  item: string;
  variant: string | null;
  lines: QuoteHoursLine[];
}

export interface QuoteHoursPhaseGroup {
  phase: string;
  items: QuoteHoursItemGroup[];
  hours: number;
  amountCents: number;
  /** Gelijk aan hours en amountCents zolang er geen van–tot prijs in de fase zit. */
  maxHours: number;
  maxAmountCents: number;
}

/**
 * Regels per fase en daarbinnen per onderdeel, in de volgorde van de offerte.
 * Alleen opeenvolgende regels worden samengevoegd: twee tabellen met dezelfde
 * titel blijven zo twee fases, zoals ze ook in de offerte staan.
 */
export function groupQuoteHoursLines(lines: QuoteHoursLine[]): QuoteHoursPhaseGroup[] {
  const phases: QuoteHoursPhaseGroup[] = [];
  let previous: QuoteHoursLine | null = null;
  for (const line of lines) {
    let phase = phases[phases.length - 1];
    if (!phase || previous?.phase !== line.phase) {
      phase = { phase: line.phase, items: [], hours: 0, amountCents: 0, maxHours: 0, maxAmountCents: 0 };
      phases.push(phase);
    }
    let item = phase.items[phase.items.length - 1];
    if (!item || previous?.item !== line.item || previous.variant !== line.variant) {
      item = { item: line.item, variant: line.variant, lines: [] };
      phase.items.push(item);
    }
    item.lines.push(line);
    phase.hours = Math.round((phase.hours + line.hours) * 100) / 100;
    phase.amountCents += line.amountCents ?? 0;
    phase.maxHours = Math.round((phase.maxHours + (line.maxHours ?? line.hours)) * 100) / 100;
    phase.maxAmountCents += line.maxAmountCents ?? line.amountCents ?? 0;
    previous = line;
  }
  return phases;
}
